import React, { useEffect, useState } from 'react';
import { useAuth } from '../../src/contexts/AuthContext';
import { api } from '../../src/services/api';
import { Profile } from '../../types';
import { Users, Shield } from 'lucide-react';
import LoadingSpinner from '../../src/components/LoadingSpinner';

const EquipeParceirosView: React.FC = () => {
    const { profile } = useAuth();
    const [users, setUsers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const data = await api.auth.listProfiles();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    if (profile?.role !== 'parceiro') {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-red-50 rounded-2xl border border-red-100 animate-fade-in">
                <Shield size={48} className="text-red-400 mb-4" />
                <h2 className="text-xl font-bold text-red-900 mb-2">Acesso Restrito</h2>
                <p className="text-red-700">Apenas parceiros podem ver esta página.</p>
            </div>
        );
    }

    if (loading) {
        return <LoadingSpinner label="Carregando equipe..." />;
    }

    const members = users.filter(u => u.role === 'vendedor_parceiro' && u.parceiro_id === profile.id);

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h2 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">Equipe de Parceiros</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Vendedores parceiros vinculados a você.</p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-3">
                        <img
                            src={profile.avatar_url || 'https://via.placeholder.com/40'}
                            alt="Avatar"
                            className="w-9 h-9 rounded-full border border-gray-200 dark:border-gray-600 object-cover"
                            onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/40')}
                        />
                        <p className="font-semibold text-gray-900 dark:text-white">{profile.full_name}</p>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                        <Users size={12} />
                        <span>{members.length} vendedor{members.length !== 1 ? 'es' : ''}</span>
                    </div>
                </div>

                {members.length === 0 ? (
                    <div className="px-6 py-4 text-sm text-gray-400 italic">Nenhum vendedor parceiro vinculado.</div>
                ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {members.map(member => (
                            <div key={member.id} className="px-6 py-3 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0">
                                    <img
                                        src={member.avatar_url || 'https://via.placeholder.com/32'}
                                        alt="Avatar"
                                        className={`w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 object-cover shrink-0 ${member.status === 'inactive' ? 'grayscale opacity-50' : ''}`}
                                        onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/32')}
                                    />
                                    <div className="min-w-0">
                                        <p className={`text-sm font-medium truncate ${member.status === 'inactive' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>{member.full_name}</p>
                                        <p className="text-xs text-gray-400 truncate">{member.job_title || member.whatsapp || 'Sem informações'}</p>
                                    </div>
                                </div>
                                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full shrink-0 ${member.status === 'inactive' ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : 'bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
                                    {member.status === 'inactive' ? 'Inativo' : 'Ativo'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EquipeParceirosView;
